import {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  getGridDateOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
  GridColDef,
  GridLogicOperator,
  GridRowSelectionModel,
  GridToolbarProps,
  GridFilterItem,
} from "@mui/x-data-grid";
import {
  currencyFormatter,
  EscalationStatus,
  EscalationTableFields,
  IEscalationResponseItem,
  INSIGHTS_UNASSIGNED,
  langauge,
  PolicyEscalationType,
  PolicyType,
  SavedFilterType,
  toDecimal,
} from "shared";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { DateTime } from "luxon";
import { Tooltip } from "@mui/material";
import { LockClosedIcon, LockOpenIcon } from "@heroicons/react/24/outline";
import { useLocation } from "react-router";
import { toast } from "react-toastify";

import {
  useEscalationParams,
  useEscalations,
  useEscalationTableModels,
} from "../../hooks/useEscalations";
import { useExportAllEscalationsMutation } from "../../services/api/escalationApi/escalation";
import { usePersistedTableModels } from "../../hooks/usePersistedTableModels";
import { ApiSavedFilterPanel } from "../Common/Datagrid/ApiSavedFilterPanel";
import { SaveFilterButton } from "../Common/Datagrid/SaveFilterButton";
import { useApplySavedFilterFromUrl } from "../../hooks/useApplySavedFilterFromUrl";
import {
  commonConfig,
  filterColumns,
  getColumnForNewFilter,
  getTogglableColumns,
  onRowSelectionChangeWrapper,
} from "../../lib/datagrid";
import { WrappedLink } from "../WrappedLink";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { NotesCellButton } from "../Molecules/NotesDrawer";
import { DatePopover } from "../../../common/Components/DatePopover";
import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import { useCurrentOrgUsers } from "../../hooks/useCurrentOrgUsers";
import { Badge } from "../../../common/Atoms/Badge";
import { CustomGenericToolbar } from "../Common/Datagrid/CustomGenericToolbar";
import { DatagridCard } from "../Common/Datagrid/DatagridCard";
import { useGroups } from "../../hooks/useGroups";
import { usePolicies } from "../../hooks/usePolicies";
import { useContactTags } from "../../hooks/useContactTags";
import { useInvoiceTags } from "../../hooks/useInvoiceTags";
import { TagBadge } from "../Molecules/TagBadge";

import { SelectedEscalationOptions } from "./SelectedEscalationsOptions";

interface EscalationTableProps {
  context?: `contact`;
}

// eslint-disable-next-line quotes
declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    rowSelectionModel: GridRowSelectionModel;
    context: `default` | `contact`;
    onRemoveFilter: (filterId: GridFilterItem[`id`]) => void;
  }
}

type ToolbarProps = GridToolbarProps & {
  rowSelectionModel: GridRowSelectionModel;
  context: `default` | `contact`;
  onRemoveFilter: (filterId: GridFilterItem[`id`]) => void;
};

const neverShowColumns = [
  EscalationTableFields.AssignedUserId,
  EscalationTableFields.Paid,
];

const frozenOptions = [
  { value: true, label: `Frozen` },
  { value: false, label: `Not Frozen` },
];

export function EscalationTable({ context }: EscalationTableProps) {
  const currentOrg = useGetSelectedOrganisation();
  const { search } = useLocation();
  const { data: groupsRes } = useGroups();
  const { data: policiesRes } = usePolicies();
  const { tags: contactTags, colorMap: contactTagColors } = useContactTags();
  const { tags: invoiceTags, colorMap: invoiceTagColors } = useInvoiceTags();
  const urlParams = new URLSearchParams(search);
  const { data: escalationsData, isLoading, isFetching } = useEscalations(`table`);
  const tableModels = useEscalationTableModels();
  const { filterModel, setFilterModel, setPaginationModel, setSortModel, sortModel, paginationModel, resetModels } = tableModels;
  const restoredModels = usePersistedTableModels(`escalation`, tableModels);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: `include`,
    ids: new Set(),
  });
  const currentOrgUsers = useCurrentOrgUsers(true);
  const apiRef = useGridApiRef();
  const defaultColumnVisibilityModel = useMemo(() => ({
    [EscalationTableFields.AssignedUserId]: false,
    [EscalationTableFields.Paid]: false,
    [EscalationTableFields.ContactName]: context === `contact` ? false : true,
    [EscalationTableFields.InvoiceReference]: false,
    [EscalationTableFields.InvoiceDate]: false,
    [EscalationTableFields.InvoiceDueDate]: false,
    [EscalationTableFields.DaysPastDue]: false,
    [EscalationTableFields.ExpectedPaymentDate]: false,
    [EscalationTableFields.AmountPaid]: false,
    [EscalationTableFields.BalanceDue]: false,
    [EscalationTableFields.ContactBalance]: false,
    [EscalationTableFields.ContactTag]: false,
    [EscalationTableFields.InvoiceTag]: false,
  }), [context]);
  const savedFilterId = useApplySavedFilterFromUrl(apiRef, SavedFilterType.Escalation, {
    defaultColumnVisibilityModel,
  });

  const escalations = escalationsData?.escalations || [];

  // On first mount, reset the filters, unless we're restoring a saved filter
  useEffect(() => {
    if (urlParams.has(`persist`) || savedFilterId || restoredModels) {
      // Do nothing
    }
    else {
      resetModels();
    }
  }, []);

  const policyOptions = useMemo(() => {
    return (policiesRes || [])
      .filter(p => [PolicyType.ESCALATION, PolicyType.CONTACT_ESCALATION].includes(p.policy.policy_type))
      .map(p => ({
        value: p.policy.id,
        label: p.policy.title || `Policy ${p.policy.id}`,
      }));
  }, [policiesRes]);

  const groupOptions = useMemo(() => {
    if (!groupsRes) {
      return [];
    }

    return groupsRes.map(group => {
      return {
        label: group.title,
        value: group.id,
      };
    });
  }, [groupsRes]);

  const contactTagOptions = useMemo(() => contactTags.map(tag => ({ value: tag, label: tag })), [contactTags]);
  const invoiceTagOptions = useMemo(() => invoiceTags.map(tag => ({ value: tag, label: tag })), [invoiceTags]);

  const onRemoveFilter = (filterId: GridFilterItem[`id`]) => {
    setFilterModel({
      items: filterModel.items.filter(item => item.id !== filterId),
    });
  };

  const columns: GridColDef<IEscalationResponseItem>[] = useMemo(() => {
    const result: GridColDef<IEscalationResponseItem>[] = [
      {
        field: EscalationTableFields.Frozen,
        type: `singleSelect`,
        headerName: ``,
        width: 40,
        sortable: false,
        // Contact level escalations have no invoice to freeze
        valueGetter: (value, row) => row.invoice?.skip || false,
        valueOptions: frozenOptions,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        valueFormatter: (value, row) => row.invoice?.skip ? `Frozen` : ``,
        renderCell: ({ row }) => {
          if (!row.invoice) return null;

          if (row.invoice.skip) {
            return (
              <Tooltip title={ `Invoice is frozen. No policies will apply.` }>
                <span className={ `w-full h-full flex items-center` }>
                  <LockClosedIcon className={ `h-5 w-5 text-blue-400` } />
                </span>
              </Tooltip>
            );
          }

          return (
            <Tooltip title={ `Invoice not frozen` }>
              <span className={ `w-full h-full flex items-center` }>
                <LockOpenIcon className={ `h-5 w-5 text-gray-200` } />
              </span>
            </Tooltip>
          );
        },
      },
      {
        field: EscalationTableFields.ScheduledDate,
        headerName: `Scheduled`,
        renderCell: ({ row }) => <DatePopover
          date={ row.escalationDate }
          labelFormatPreset={ DateTime.DATETIME_MED }
          organisationTimezone={ currentOrg?.validatedTimezone }
        />,
        filterOperators: getGridDateOperators(true).filter(op => {
          return [ `onOrAfter`, `onOrBefore`].includes(op.value);
        }),
        width: 120,
      },
      {
        field: EscalationTableFields.Type,
        type: `singleSelect`,
        headerName: `Type`,
        width: 70,
        align: `center`,
        headerAlign: `center`,
        valueGetter: (value, row) => row.escalationType,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        valueOptions: Object.keys(PolicyEscalationType).map(key => ({
          value: PolicyEscalationType[key] as PolicyEscalationType,
          label: langauge.escalation.type[PolicyEscalationType[key] as PolicyEscalationType]?.label || key,
        })),
        renderCell: ({ row, value }) => (
          <Tooltip title={ langauge.escalation.type[value as PolicyEscalationType]?.label || value }>
            <span>
              <WrappedLink
                to={ `/escalations/${ row.id }/contact/${row.contactId}` }
              >
                <Paragraph
                  variant={ `link` }
                  as={ `span` }
                >
                  { langauge.escalation.type[value as PolicyEscalationType]?.icon || value }
                </Paragraph>
              </WrappedLink>
            </span>
          </Tooltip>
        ),
      },
      {
        field: EscalationTableFields.PolicyName,
        headerName: `Policy`,
        type: `singleSelect`,
        width: 200,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        valueOptions: policyOptions,
        valueGetter: (value, row) => row.policyId,
        renderCell: ({ row }) => (
          <WrappedLink
            to={ `/escalations/${ row.id }/contact/${row.contactId}` }
          >
            <Paragraph
              variant={ `link` }
              as={ `span` }
            >
              { row.policyId ? row.policy?.title : `Manual` }
            </Paragraph>
          </WrappedLink>
        ),
      },
      {
        field: EscalationTableFields.ContactName,
        headerName: `Contact`,
        type: `string`,
        width: 160,
        valueGetter: (value, row) => row.contact.name,
        filterOperators: getGridStringOperators().filter(op => op.value === `contains`),
        renderCell: ({ row }) => (
          <WrappedLink
            to={ `/contacts/${ row.contactId }` }
          >
            <Paragraph
              variant={ `link` }
              as={ `span` }
            >
              { row.contact?.name }
            </Paragraph>
          </WrappedLink>
        ),
      },
      {
        field: EscalationTableFields.InvoiceNumber,
        headerName: `Invoice`,
        type: `string`,
        width: 160,
        valueGetter: (value, row) => row.invoice?.xero_number,
        filterOperators: getGridStringOperators().filter(op => op.value === `contains`),
        renderCell: ({ row }) => (
          <WrappedLink
            to={ `/invoices/${ row.invoiceId }` }
          >
            <Paragraph
              variant={ `link` }
              as={ `span` }
            >
              { row.invoice?.xero_number }
            </Paragraph>
          </WrappedLink>
        ),
      },
      {
        field: EscalationTableFields.InvoiceReference,
        headerName: `Reference`,
        type: `string`,
        width: 140,
        valueGetter: (value, row) => row.invoice?.xero_reference || ``,
        sortable: false,
        filterable: false,
      },
      {
        field: EscalationTableFields.InvoiceDate,
        headerName: `Invoice Date`,
        valueGetter: (value, row) => row.invoice?.issue_date_string,
        sortable: false,
        filterable: false,
        width: 110,
      },
      {
        field: EscalationTableFields.InvoiceDueDate,
        headerName: `Due Date`,
        valueGetter: (value, row) => row.invoice?.due_date_string,
        sortable: false,
        filterable: false,
        width: 110,
      },
      {
        field: EscalationTableFields.DaysPastDue,
        headerName: `Days Overdue`,
        type: `number`,
        width: 90,
        valueGetter: (value, row) => {
          if (row.invoice?.xero_status !== `AUTHORISED`) return null;
          if (!row.invoice?.due_date_string) return null;
          if (!currentOrg?.currentTime) return null;

          const dueDate = new Date(row.invoice.due_date_string);
          const now = new Date(currentOrg.currentTime);
          const diffTime = now.getTime() - dueDate.getTime();
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

          return diffDays;
        },
        sortable: false,
        filterable: false,
      },
      {
        field: EscalationTableFields.ExpectedPaymentDate,
        headerName: `Expected Payment`,
        valueGetter: (value, row) => row.invoice?.expected_payment_date_str,
        sortable: false,
        filterable: false,
        width: 140,
      },
      {
        field: EscalationTableFields.AmountPaid,
        headerName: `Amount Paid`,
        type: `number`,
        width: 120,
        valueGetter: (value, row) => {
          // Contact-level escalations have no invoice
          if (!row.invoice) return null;
          if (!row.invoice.xero_amount_total || row.invoice.xero_amount_due === undefined) return 0;

          return toDecimal(row.invoice.xero_amount_total - row.invoice.xero_amount_due);
        },
        valueFormatter: (value, row) => {
          if (value === null || value === undefined) return ``;

          const currency = row.invoice?.xero_currency;

          return currencyFormatter(currency)(value);
        },
        sortable: false,
        filterable: false,
      },
      {
        field: EscalationTableFields.BalanceDue,
        headerName: `Invoice Balance`,
        type: `number`,
        width: 120,
        // Contact-level escalations have no invoice
        valueGetter: (value, row) => row.invoice ? toDecimal(row.invoice.xero_amount_due || 0) : null,
        valueFormatter: (value, row) => {
          if (value === null || value === undefined) return ``;

          return currencyFormatter(row.invoice?.xero_currency)(value);
        },
        filterable: false,
      },
      {
        field: EscalationTableFields.ContactBalance,
        headerName: `Contact Balance`,
        type: `number`,
        width: 130,
        valueGetter: (value, row) => toDecimal(row.contact?.outstandingBalanceHome || 0),
        valueFormatter: value => currencyFormatter(currentOrg?.baseCurrency)(value),
        filterable: false,
      },
      {
        field: EscalationTableFields.Assignee,
        type: `singleSelect`,
        headerName: `Assignee`,
        width: 140,
        valueGetter: (value, row) => row.assignedToUserId,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        valueOptions: [{ value: INSIGHTS_UNASSIGNED, label: `Unassigned` }, ...currentOrgUsers],
      },
      {
        field: EscalationTableFields.Status,
        type: `singleSelect`,
        headerName: `Status`,
        width: 120,
        valueGetter: (value, row) => row.escalationStatus,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        valueOptions: Object.keys(EscalationStatus).map(key => ({
          value: EscalationStatus[key] as EscalationStatus,
          label: langauge.escalation.status[EscalationStatus[key] as EscalationStatus] || key,
        })),
        renderCell: ({ row }) => getEscalationDisplayStatus(row),
      },
      {
        field: EscalationTableFields.LatestNote,
        headerName: `Latest Note`,
        type: `string`,
        width: 220,
        sortable: false,
        filterable: false,
        valueGetter: (value, row) => row.notes?.find(note => note.escalationId === row.id)?.content || ``,
        renderCell: ({ value }) => {
          if (!value) return null;

          return (
            <Tooltip title={ value }>
              <span>{ value }</span>
            </Tooltip>
          );
        },
      },
      {
        field: `notes`,
        headerName: `Notes`,
        width: 70,
        sortable: false,
        filterable: false,
        disableExport: true,
        align: `center`,
        headerAlign: `center`,
        renderCell: ({ row }) => <NotesCellButton resource={ { type: `escalation`, id: row.id } } />,
      },
      {
        field: EscalationTableFields.GroupId,
        headerName: `Group`,
        description: `Paidnice Group`,
        type: `singleSelect`,
        width: 150,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is` || op.value === `not`),
        valueOptions: groupOptions,
        valueGetter: (value, row) => row.group.id,
        renderCell: ({ row }) => (
          <WrappedLink
            to={ `/groups/${ row.group.id }` }
          >
            <Paragraph
              variant={ `link` }
              as={ `span` }
            >
              { row.group.title }
            </Paragraph>
          </WrappedLink>
        ),
      },

      {
        field: EscalationTableFields.ContactTag,
        headerName: `Contact Tags`,
        type: `singleSelect`,
        width: 160,
        sortable: false,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        valueOptions: contactTagOptions,
        valueGetter: (value, row) => row.contact?.tags?.join(`, `) || ``,
        renderCell: ({ row }) => (
          <div className={ `flex flex-wrap items-center h-full gap-1` }>
            { (row.contact?.tags || []).map(tag => (
              <TagBadge
                key={ tag }
                name={ tag }
                color={ contactTagColors[tag] }
              />
            )) }
          </div>
        ),
      },
      {
        field: EscalationTableFields.InvoiceTag,
        headerName: `Invoice Tags`,
        type: `singleSelect`,
        width: 160,
        sortable: false,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        valueOptions: invoiceTagOptions,
        valueGetter: (value, row) => (row.invoice?.tags as { name: string }[] | undefined)?.map(t => t.name).join(`, `) || ``,
        renderCell: ({ row }) => (
          <div className={ `flex flex-wrap items-center h-full gap-1` }>
            { ((row.invoice?.tags || []) as { name: string }[]).map(({ name }) => (
              <TagBadge
                key={ name }
                name={ name }
                color={ invoiceTagColors[name] }
              />
            )) }
          </div>
        ),
      },

      // Hidden
      {
        field: EscalationTableFields.AssignedUserId,
        headerName: ``,
        type: `string`,
        sortable: false,
        filterable: false,
      },
      {
        field: EscalationTableFields.Paid,
        headerName: `No Balance`,
        type: `singleSelect`,
        width: 100,
        valueGetter: (value, row) => {
          if (row.contact?.outstandingBalanceHome <= 0) {
            return true;
          }

          return false;
        },
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        valueOptions: [
          {
            value: true,
            label: `Include contacts with no balance`,
          },
        ],
      },
    ];

    return result;
  }, [currentOrgUsers, currentOrg, groupOptions, policyOptions, contactTagOptions, invoiceTagOptions, contactTagColors, invoiceTagColors]);

  const count = escalationsData?.total ? Number(escalationsData.total) : 0;
  const viewportHeight = window.innerHeight;
  const tableHeight = viewportHeight
    - 190; // px for the header

  return (
    <div
      style={ { height: tableHeight } }
    >
      <DatagridCard className={ `h-full` }>
        <DataGridPro
          apiRef={ apiRef }
          rowCount={ count }
          rows={ escalations }
          columns={ columns }
          loading={ isLoading || isFetching }
          // Filtering
          filterMode={ `server` }
          filterModel={ filterModel }
          onFilterModelChange={ setFilterModel }
          // Sorting
          sortingMode={ `server` }
          sortModel={ sortModel }
          onSortModelChange={ setSortModel }
          // Pagination
          paginationMode={ `server` }
          paginationModel={ paginationModel }
          onPaginationModelChange={ setPaginationModel }
          pageSizeOptions={ [25, 50, 100, 300] }
          pagination
          // Checkboxes
          checkboxSelection
          onRowSelectionModelChange={ model => onRowSelectionChangeWrapper(model, escalations.map(i => i.id), setRowSelectionModel) }
          rowSelectionModel={ rowSelectionModel }
          disableRowSelectionOnClick
          // Toolbar
          showToolbar
          // Customizations
          rowHeight={ 38 }
          sx={ {
            ...commonConfig.headerStyle,
            ...commonConfig.toolbarStyle,
            '--DataGrid-overlayHeight': `300px`,
          } }
          slots={ {
            toolbar: CustomToolbar,
            filterPanel: ApiSavedFilterPanel,
          } }
          slotProps={ {
            toolbar: {
              showQuickFilter: true,
              rowSelectionModel,
              onRemoveFilter,
              context,
            },
            filterPanel: {
              savedFilterType: SavedFilterType.Escalation,
              logicOperators: [GridLogicOperator.And],
              getColumnForNewFilter: getColumnForNewFilter,
              filterFormProps: {
                filterColumns: args => filterColumns(args, context === `contact` ? [
                  EscalationTableFields.ContactName,
                  EscalationTableFields.ContactTag,
                ]: []),
              },
            },
            columnsManagement: {
              getTogglableColumns: cols => getTogglableColumns(cols, neverShowColumns),
            },
            pagination: {
              showFirstButton: true,
              showLastButton: true,
            },
          } }
          initialState={ {
            columns: {
              columnVisibilityModel: defaultColumnVisibilityModel,
            },
          } }
        />
      </DatagridCard>
    </div>
  );
}

export function getEscalationDisplayStatus(e: IEscalationResponseItem) {
  let suffix = ``;

  if (e.invoice?.xero_amount_due === 0) {
    suffix = ` (Paid)`;
  }

  if (e.contact?.outstandingBalanceHome <= 0) {
    suffix = ` (No Balance)`;
  }

  const readableStatus = langauge.escalation.status[e.escalationStatus as EscalationStatus] || e.escalationStatus;

  const statusColors: { [key in EscalationStatus]: string } = {
    [EscalationStatus.PENDING]: `amber`,
    [EscalationStatus.COMPLETE]: `green`,
    [EscalationStatus.CANCELLED]: `gray`,
  };

  return (<Badge
    className={ `max-w-fit` }
    color={ statusColors[e.escalationStatus as EscalationStatus] || `gray` }
    message={ `${readableStatus}${suffix}` }
    size={ `sm` }
  />);
}

function CustomToolbar({ rowSelectionModel, onRemoveFilter }: ToolbarProps) {
  const params = useEscalationParams(`table`);
  const [exportAllEscalations] = useExportAllEscalationsMutation();

  async function onExportAll(filename: string | undefined, columns: string[]) {
    // Same params the table query uses, minus paging — the export covers every page
    try {
      const response = await exportAllEscalations({
        ...params,
        page: undefined,
        limit: undefined,
        filename,
        columns,
      }).unwrap();

      toast.success(`Export queued. The CSV will be emailed to ${response.email} shortly.`);
    }
    catch (e) {
      console.error(e);
      toast.error(`Could not queue the export. Please try again.`);
    }
  }

  return (
    <CustomGenericToolbar
      rowSelectionModel={ rowSelectionModel }
      onRemoveFilter={ onRemoveFilter }
      onExportAll={ onExportAll }
      context={ `escalations` }
      leftActions={ <SaveFilterButton type={ SavedFilterType.Escalation } /> }
    >
      <div className={ `` }>
        { /* Selected row actions */ }
        <div>
          <SelectedEscalationOptions
            rowSelectionModel={ rowSelectionModel }
          />
        </div>
      </div>
    </CustomGenericToolbar>
  );
}
